import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useUpgradePrompt } from '@/components/upgrade-prompt'
import { isFreeTier } from '@/lib/subscription-access'
import { apiV2, ApiV2Error, type V2Member, type V2Invitation } from '@/lib/api-v2-client'
import { useAuth } from '@/hooks/use-auth'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/dashboard/team/')({
  component: TeamPage,
})

function TeamPage() {
  const { user, organization } = useAuth()
  const orgId = organization?.id ?? ''

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['team-members', orgId],
    queryFn: () => apiV2.listMembers(),
    enabled: !!orgId,
  })

  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    queryKey: ['team-invitations', orgId],
    queryFn: () => apiV2.listInvitations(),
    enabled: !!orgId,
  })

  const members = membersData?.data ?? []
  const invitations = invitationsData?.data ?? []
  const currentUserIsManager = members.find(m => m.id === user?.id)?.can_manage_team ?? false

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Team</h1>
          <p className="text-muted-foreground">
            Manage your team members and invitations
          </p>
        </div>
        {currentUserIsManager && (
          <InviteDialog orgId={orgId} />
        )}
      </div>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            {members.length} member{members.length !== 1 ? 's' : ''} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : (
            <div className="divide-y">
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  orgId={orgId}
                  currentUserId={user?.id ?? ''}
                  canManage={currentUserIsManager}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {currentUserIsManager && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
            <CardDescription>
              {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitationsLoading ? (
              <div className="h-14 animate-pulse rounded bg-muted" />
            ) : invitations.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">No pending invitations</p>
            ) : (
              <div className="divide-y">
                {invitations.map((invitation) => (
                  <InvitationRow
                    key={invitation.id}
                    invitation={invitation}
                    orgId={orgId}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function MemberRow({
  member,
  orgId,
  currentUserId,
  canManage,
}: {
  member: V2Member
  orgId: string
  currentUserId: string
  canManage: boolean
}) {
  const queryClient = useQueryClient()

  const updateRoleMutation = useMutation({
    mutationFn: (role: string) => apiV2.updateMemberRole(orgId, member.id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', orgId] })
      toast.success('Role updated')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update role'),
  })

  const removeMutation = useMutation({
    mutationFn: () => apiV2.removeMember(orgId, member.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', orgId] })
      toast.success('Member removed')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove member'),
  })

  const isSelf = member.id === currentUserId
  const canChangeRole = canManage && !member.is_owner && !isSelf
  const canRemove = canManage && !member.is_owner && !isSelf

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-medium">
          {member.name?.[0]?.toUpperCase() ?? '?'}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{member.name}</span>
            {member.is_owner && <Badge variant="secondary">Owner</Badge>}
            {isSelf && <Badge variant="outline">You</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canChangeRole ? (
          <Select
            value={member.role}
            onValueChange={(role) => updateRoleMutation.mutate(role)}
            disabled={updateRoleMutation.isPending}
          >
            <SelectTrigger className="w-32" aria-label={`Role for ${member.name || member.email}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="editor">Editor</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
              <SelectItem value="notify_only">Notify Only</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="outline" className="capitalize">
            {member.role.replace('_', ' ')}
          </Badge>
        )}
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => removeMutation.mutate()}
            disabled={removeMutation.isPending}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  )
}

function InvitationRow({
  invitation,
  orgId,
}: {
  invitation: V2Invitation
  orgId: string
}) {
  const queryClient = useQueryClient()

  const cancelMutation = useMutation({
    mutationFn: () => apiV2.cancelInvitation(orgId, invitation.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations', orgId] })
      toast.success('Invitation cancelled')
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to cancel invitation'),
  })

  const expiresAt = invitation.expires_at ? new Date(invitation.expires_at) : null
  const isExpired = expiresAt != null && expiresAt < new Date()

  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-medium">{invitation.email}</span>
          <Badge variant="outline" className="capitalize">
            {invitation.role.replace('_', ' ')}
          </Badge>
          {isExpired && <Badge variant="destructive">Expired</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          Expires {expiresAt ? expiresAt.toLocaleDateString() : 'never'}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={() => cancelMutation.mutate()}
        disabled={cancelMutation.isPending}
      >
        Cancel
      </Button>
    </div>
  )
}

function InviteDialog({ orgId }: { orgId: string }) {
  const { show: showUpgradePrompt } = useUpgradePrompt()
  const { organization } = useAuth()
  const freeTier = isFreeTier(organization)
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('editor')
  const queryClient = useQueryClient()

  const inviteMutation = useMutation({
    mutationFn: () => apiV2.createInvitation(orgId, { email, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations', orgId] })
      toast.success('Invitation sent')
      setEmail('')
      setRole('editor')
      setOpen(false)
    },
    onError: (err) => {
      if (err instanceof ApiV2Error && err.status === 402) {
        setOpen(false)
        showUpgradePrompt(err.message)
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to send invitation')
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Invite Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join your organization.
            {freeTier && ' The free plan includes 1 team member. Pro includes 3.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="notify_only">Notify Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => inviteMutation.mutate()}
            disabled={!email || inviteMutation.isPending}
          >
            {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
